/**
 * 根据文件头 magic 识别真实音频容器，纠正假 .mp3（实为 M4A）等上传/缓存问题。
 */

const AUDIO_FALLBACK = { mime: 'audio/mpeg', ext: 'mp3' };

/**
 * @param {ArrayBuffer|ArrayBufferView|Blob|File} input
 * @returns {Promise<{mime: string, ext: string}|null>}
 */
export async function sniffAudioFormat(input) {
  const bytes = await readHeaderBytes(input, 64);
  if (!bytes || bytes.length < 12) {
    return null;
  }
  return sniffAudioFormatFromBytes(bytes);
}

/**
 * @param {Uint8Array} bytes
 * @returns {{mime: string, ext: string}|null}
 */
export function sniffAudioFormatFromBytes(bytes) {
  if (!bytes || bytes.length < 12) return null;

  // ID3… → MP3
  if (bytes[0] === 0x49 && bytes[1] === 0x44 && bytes[2] === 0x33) {
    return { mime: 'audio/mpeg', ext: 'mp3' };
  }

  // MPEG frame sync 0xFFEx
  if (bytes[0] === 0xff && (bytes[1] & 0xe0) === 0xe0) {
    return { mime: 'audio/mpeg', ext: 'mp3' };
  }

  // ftyp.... → MP4/M4A（跳过 4 字节 size）
  if (bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) {
    const brand = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]);
    if (/^(M4A |M4B |mp42|isom|iso2|mp41)/i.test(brand) || brand.includes('M4A')) {
      return { mime: 'audio/mp4', ext: 'm4a' };
    }
    // 其它 ftyp 仍按 AAC-in-MP4 音频处理更稳妥（本站音频上传场景）
    return { mime: 'audio/mp4', ext: 'm4a' };
  }

  // fLaC
  if (bytes[0] === 0x66 && bytes[1] === 0x4c && bytes[2] === 0x61 && bytes[3] === 0x43) {
    return { mime: 'audio/flac', ext: 'flac' };
  }

  // RIFF....WAVE
  if (
    bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
    bytes[8] === 0x57 && bytes[9] === 0x41 && bytes[10] === 0x56 && bytes[11] === 0x45
  ) {
    return { mime: 'audio/wav', ext: 'wav' };
  }

  // OggS
  if (bytes[0] === 0x4f && bytes[1] === 0x67 && bytes[2] === 0x67 && bytes[3] === 0x53) {
    return { mime: 'audio/ogg', ext: 'ogg' };
  }

  return null;
}

/**
 * 将 File/Blob 纠成与内容一致的扩展名与 MIME（视频跳过）。
 * @param {File|Blob} file
 * @param {{isVideo?: boolean}} [options]
 * @returns {Promise<File>}
 */
export async function normalizeAudioFileForUpload(file, options = {}) {
  if (!file || options.isVideo) {
    return file instanceof File ? file : new File([file], 'audio.mp3', { type: AUDIO_FALLBACK.mime });
  }

  const sniffed = await sniffAudioFormat(file);
  if (!sniffed) {
    return file instanceof File
      ? file
      : new File([file], 'audio.mp3', { type: file.type || AUDIO_FALLBACK.mime });
  }

  const currentName = file instanceof File && file.name ? file.name : `audio.${sniffed.ext}`;
  const base = currentName.includes('.')
    ? currentName.slice(0, currentName.lastIndexOf('.'))
    : currentName;
  const nextName = `${base}.${sniffed.ext}`;
  const currentType = (file.type || '').toLowerCase();
  const alreadyOk =
    currentName.toLowerCase().endsWith(`.${sniffed.ext}`) &&
    (currentType === sniffed.mime || currentType === 'audio/x-m4a' && sniffed.mime === 'audio/mp4');

  if (alreadyOk && file instanceof File) {
    return file;
  }

  console.log('🎵 [audioFormatSniff] 纠正上传音频格式:', {
    from: { name: currentName, type: file.type || '(empty)' },
    to: { name: nextName, type: sniffed.mime }
  });

  return new File([file], nextName, {
    type: sniffed.mime,
    lastModified: file.lastModified || Date.now()
  });
}

/**
 * 用 sniff 结果重包 Blob（缓存播放用）。
 * @param {Blob|ArrayBuffer|ArrayBufferView} data
 * @param {string} [fallbackMime]
 * @returns {Promise<Blob>}
 */
export async function retypeAudioBlob(data, fallbackMime = AUDIO_FALLBACK.mime) {
  const sniffed = await sniffAudioFormat(data);
  const mime = sniffed?.mime || fallbackMime;
  if (data instanceof Blob) {
    if (data.type === mime) return data;
    return new Blob([data], { type: mime });
  }
  return new Blob([data], { type: mime });
}

async function readHeaderBytes(input, length) {
  if (!input) return null;

  if (input instanceof ArrayBuffer) {
    return new Uint8Array(input.slice(0, length));
  }
  if (ArrayBuffer.isView(input)) {
    const view = input;
    const end = Math.min(view.byteLength, length);
    return new Uint8Array(view.buffer, view.byteOffset, end);
  }
  if (typeof Blob !== 'undefined' && input instanceof Blob) {
    const slice = input.slice(0, length);
    const buf = await slice.arrayBuffer();
    return new Uint8Array(buf);
  }
  return null;
}
